import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { fetchUserTree, fetchUserTreeSummary, subscribeToTreeRefresh } from '../services/userTreeService';
import { fetchAnalyticsRecords, fetchUserAggregates } from '../services/dashboardService';
import { fetchAdminGameAnalytics } from '../services/adminAnalyticsService';
import { cutoffWeight, getPeriodWindows } from '../utils/dashboardPeriods';
import {
  buildBreakdownRows,
  buildDirectReportIndex,
  buildSeries,
  rankPlayers,
  summarizeRecords,
  totalsByDirectReport,
  totalsByUser,
  totalsFromUserAggregates
} from '../utils/dashboardMetrics';
import { buildPlayerLookup, collectPlayerUsernames, defaultCurrency, summarizeNetwork } from '../utils/dashboardTree';

const CACHE_TTL_MS = 60 * 1000;
const AUTO_REFRESH_MS = 5 * 60 * 1000;
const LIVE_END_STEP_MS = 5 * 60 * 1000;
export const AUTO_PLAYER_TOTALS_LIMIT = 4000;

const IDLE = { key: null, status: 'idle', data: null, error: null };

// Module-level so leaving the dashboard and coming back within the TTL is instant
const responseCache = new Map();

const readCache = (key) => {
  const entry = responseCache.get(key);
  if (entry && Date.now() - entry.at < CACHE_TTL_MS) return entry.value;
  responseCache.delete(key);
  return null;
};

const writeCache = (key, value) => responseCache.set(key, { at: Date.now(), value });

const getIdToken = async () => (await fetchAuthSession()).tokens?.idToken?.toString();

/**
 * End of a live request, rounded up to 5 minutes. The analytics Lambda caches each exact range for
 * 30 minutes, so a fixed end-of-day would keep answering refreshes with the same numbers.
 */
const liveEnd = (range) =>
  new Date(Math.min(range.fetchEnd.getTime(), Math.ceil(Date.now() / LIVE_END_STEP_MS) * LIVE_END_STEP_MS));

const buildScope = (tree) => {
  const children = tree.children || [];
  const playerIds = collectPlayerUsernames(tree);
  const directReports = children.map((child) => child.username);
  return {
    key: `${tree.username}|${directReports.join(',')}|${playerIds.length}`,
    isAdminRoot: tree.type === 'admin',
    directReports,
    playerIds,
    playerSet: new Set(playerIds),
    reportsArePlayers: children.length > 0 && children.every((child) => child.type === 'player')
  };
};

async function loadAnalytics(scope, windows, currency) {
  const token = await getIdToken();
  const { current, previous } = windows;

  if (scope.directReports.length > 0) {
    // An empty "today" just after midnight doesn't mean the rollups are missing, so both periods decide
    const [currentRows, previousRows] = await Promise.all([
      fetchAnalyticsRecords({
        userIds: scope.directReports,
        start: current.start,
        end: windows.isLive ? liveEnd(current) : current.fetchEnd,
        currency,
        token
      }),
      fetchAnalyticsRecords({ userIds: scope.directReports, start: previous.start, end: previous.end, currency, token })
    ]);
    if (currentRows.length || previousRows.length) {
      return { mode: 'rollup', playerLevel: scope.reportsArePlayers, current: currentRows, previous: previousRows };
    }
  }

  if (scope.playerIds.length === 0) return { mode: 'none', playerLevel: true, current: [], previous: [] };

  const request = { userIds: scope.playerIds, fetchAllPlayers: scope.isAdminRoot, currency, token };
  const [currentRows, previousRows] = await Promise.all([
    fetchAnalyticsRecords({ ...request, start: current.start, end: current.fetchEnd }),
    fetchAnalyticsRecords({ ...request, start: previous.start, end: previous.end })
  ]);
  return { mode: 'players', playerLevel: true, current: currentRows, previous: previousRows };
}

/**
 * Everything the dashboard shows: the user tree, analytics for the selected period and the one it is
 * compared with, per-player totals (loaded after the main numbers), and platform-wide game stats for
 * the Admins group.
 */
export default function useDashboardData() {
  const [tree, setTree] = useState(null);
  const [treeStatus, setTreeStatus] = useState('loading');
  const [currency, setCurrency] = useState(null);
  const [period, setPeriodState] = useState('today');
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [isAdminGroup, setIsAdminGroup] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(IDLE);
  const [aggregates, setAggregates] = useState(IDLE);
  const [platform, setPlatform] = useState(IDLE);
  const [manualPlayersKey, setManualPlayersKey] = useState(null);
  const forcedTickRef = useRef(null);
  const lastLoadedRef = useRef(0);
  const scopeRef = useRef(null);
  const requestSeq = useRef({ analytics: 0, aggregates: 0, platform: 0 });

  useEffect(() => {
    let cancelled = false;
    const applyTree = (fresh) => {
      if (cancelled || !fresh || fresh.summary) return;
      setTree(fresh);
      setTreeStatus('ready');
      setCurrency((selected) => selected || defaultCurrency(fresh.attributes?.currencies));
    };
    const unsubscribe = subscribeToTreeRefresh(applyTree);

    (async () => {
      if (!localStorage.getItem('userTreeServiceCache')) {
        try {
          const summary = await fetchUserTreeSummary();
          if (!cancelled && summary?.attributes?.currencies?.length) {
            setCurrency((selected) => selected || defaultCurrency(summary.attributes.currencies));
          }
        } catch (error) {
          console.warn('Dashboard: tree summary skipped', error);
        }
      }
      try {
        const fresh = await fetchUserTree(false, { staleWhileRevalidate: true });
        if (!fresh) throw new Error('Empty user tree');
        applyTree(fresh);
      } catch (error) {
        console.error('Dashboard: failed to load the user tree', error);
        if (!cancelled) setTreeStatus((status) => (status === 'ready' ? status : 'error'));
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAuthSession()
      .then((session) => {
        const groups = session.tokens?.idToken?.payload?.['cognito:groups'] || [];
        if (!cancelled) setIsAdminGroup(groups.includes('Admins'));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const scope = useMemo(() => (tree ? buildScope(tree) : null), [tree]);
  const windows = useMemo(() => ({ ...getPeriodWindows(period, new Date(nowTick)), tick: nowTick }), [period, nowTick]);
  const requestKey = scope && currency ? `${scope.key}|${currency}|${windows.id}|${windows.current.firstDay}` : null;

  // A background tree refresh hands over a new tree object; only a different key should refetch
  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);

  useEffect(() => {
    if (!requestKey) return;
    const seq = ++requestSeq.current.analytics;
    const cacheKey = `analytics|${requestKey}`;
    const cached = forcedTickRef.current === windows.tick ? null : readCache(cacheKey);
    if (cached) {
      setAnalytics({ key: requestKey, status: 'ready', data: cached, error: null });
      return;
    }

    setAnalytics((state) => (state.key === requestKey && state.data
      ? { ...state, status: 'refreshing', error: null }
      : { key: requestKey, status: 'loading', data: null, error: null }));

    loadAnalytics(scopeRef.current, windows, currency)
      .then((result) => {
        const data = { ...result, fetchedAt: Date.now() };
        writeCache(cacheKey, data);
        if (seq !== requestSeq.current.analytics) return;
        lastLoadedRef.current = data.fetchedAt;
        setAnalytics({ key: requestKey, status: 'ready', data, error: null });
      })
      .catch((error) => {
        console.error('Dashboard: failed to load analytics', error);
        if (seq !== requestSeq.current.analytics) return;
        setAnalytics((state) => ({
          key: requestKey,
          status: 'error',
          data: state.key === requestKey ? state.data : null,
          error: error.message || 'Failed to load analytics'
        }));
      });
  }, [requestKey, currency, windows]);

  const analyticsData = analytics.key === requestKey ? analytics.data : null;
  const analyticsStatus = !requestKey ? 'idle' : analytics.key === requestKey ? analytics.status : 'loading';
  const needsAggregates = Boolean(analyticsData && !analyticsData.playerLevel && scope?.playerIds.length);
  const aggregatesWanted = needsAggregates
    && (scope.playerIds.length <= AUTO_PLAYER_TOTALS_LIMIT || manualPlayersKey === requestKey);

  useEffect(() => {
    if (!aggregatesWanted) return;
    const activeScope = scopeRef.current;
    const seq = ++requestSeq.current.aggregates;
    const cacheKey = `players|${requestKey}`;
    const cached = forcedTickRef.current === windows.tick ? null : readCache(cacheKey);
    if (cached) {
      setAggregates({ key: requestKey, status: 'ready', data: cached, error: null });
      return;
    }

    setAggregates((state) => (state.key === requestKey && state.data
      ? { ...state, status: 'refreshing', error: null }
      : { key: requestKey, status: 'loading', data: null, error: null }));

    (async () => {
      try {
        const token = await getIdToken();
        const aggregateMap = await fetchUserAggregates({
          userIds: activeScope.playerIds,
          fetchAllPlayers: activeScope.isAdminRoot,
          start: windows.current.start,
          end: windows.current.fetchEnd,
          currency,
          token
        });
        const totals = totalsFromUserAggregates(aggregateMap);
        writeCache(cacheKey, totals);
        if (seq === requestSeq.current.aggregates) setAggregates({ key: requestKey, status: 'ready', data: totals, error: null });
      } catch (error) {
        console.error('Dashboard: failed to load player totals', error);
        if (seq !== requestSeq.current.aggregates) return;
        setAggregates((state) => ({
          key: requestKey,
          status: 'error',
          data: state.key === requestKey ? state.data : null,
          error: error.message || 'Failed to load players'
        }));
      }
    })();
  }, [aggregatesWanted, requestKey, currency, windows]);

  useEffect(() => {
    if (!isAdminGroup || !currency) return;
    const key = `${currency}|${windows.id}|${windows.current.firstDay}`;
    const seq = ++requestSeq.current.platform;
    const cacheKey = `platform|${key}`;
    const cached = forcedTickRef.current === windows.tick ? null : readCache(cacheKey);
    if (cached) {
      setPlatform({ key, status: 'ready', data: cached, error: null });
      return;
    }

    setPlatform((state) => (state.key === key && state.data
      ? { ...state, status: 'refreshing', error: null }
      : { key, status: 'loading', data: null, error: null }));

    fetchAdminGameAnalytics({ startDate: windows.current.start, endDate: windows.current.fetchEnd, currency, tab: 'overview' })
      .then((data) => {
        writeCache(cacheKey, data);
        if (seq === requestSeq.current.platform) setPlatform({ key, status: 'ready', data, error: null });
      })
      .catch((error) => {
        console.error('Dashboard: failed to load platform analytics', error);
        if (seq !== requestSeq.current.platform) return;
        setPlatform((state) => ({
          key,
          status: 'error',
          data: state.key === key ? state.data : null,
          error: error.message || 'Failed to load platform analytics'
        }));
      });
  }, [isAdminGroup, currency, windows]);

  const forceReload = useCallback(() => {
    const tick = Date.now();
    forcedTickRef.current = tick;
    setNowTick(tick);
  }, []);

  useEffect(() => {
    if (period !== 'today') return undefined;
    const reloadIfVisible = () => {
      if (document.visibilityState === 'visible') forceReload();
    };
    const reloadIfStale = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastLoadedRef.current > AUTO_REFRESH_MS) forceReload();
    };
    const interval = setInterval(reloadIfVisible, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', reloadIfStale);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', reloadIfStale);
    };
  }, [period, forceReload]);

  const setPeriod = useCallback((next) => {
    setPeriodState(next);
    setNowTick(Date.now());
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const fresh = await fetchUserTree(true);
      if (fresh && !fresh.summary) setTree(fresh);
    } catch (error) {
      console.error('Dashboard: refresh failed', error);
    } finally {
      forceReload();
      setRefreshing(false);
    }
  }, [forceReload]);

  const loadPlayerTotals = useCallback(() => setManualPlayersKey(requestKey), [requestKey]);

  const network = useMemo(() => (tree && currency ? summarizeNetwork(tree, currency) : null), [tree, currency]);
  const playerLookup = useMemo(() => (tree ? buildPlayerLookup(tree) : new Map()), [tree]);
  const reportIndex = useMemo(() => (tree ? buildDirectReportIndex(tree) : null), [tree]);

  const metrics = useMemo(() => {
    if (!analyticsData || !reportIndex) return null;
    const { current, previous } = windows;
    return {
      summary: summarizeRecords(analyticsData.current),
      previousToDate: summarizeRecords(analyticsData.previous, cutoffWeight(previous, previous.cutoff)),
      series: buildSeries(analyticsData.current, current),
      breakdown: buildBreakdownRows(reportIndex, totalsByDirectReport(analyticsData.current, reportIndex), null)
    };
  }, [analyticsData, reportIndex, windows]);

  const players = useMemo(() => {
    const withNames = (list) => list.map((player) => ({ ...player, ...(playerLookup.get(player.username) || { name: player.username }) }));
    const ranked = (totals) => {
      const ranking = rankPlayers(totals, { playerUsernames: scope?.playerSet });
      return { ...ranking, top: withNames(ranking.top), winners: withNames(ranking.winners) };
    };

    if (!analyticsData) return { status: analyticsStatus === 'error' ? 'error' : 'loading', ranking: null };
    if (analyticsData.playerLevel) return { status: 'ready', ranking: ranked(totalsByUser(analyticsData.current)) };
    if (!scope?.playerIds.length) return { status: 'ready', ranking: ranked(new Map()) };
    if (!aggregatesWanted) return { status: 'manual', ranking: null };
    if (aggregates.key !== requestKey || !aggregates.data) {
      return { status: aggregates.key === requestKey && aggregates.status === 'error' ? 'error' : 'loading', ranking: null };
    }
    return { status: aggregates.status, ranking: ranked(aggregates.data) };
  }, [analyticsData, analyticsStatus, scope, aggregatesWanted, aggregates, requestKey, playerLookup]);

  return {
    tree,
    treeStatus,
    currency,
    setCurrency,
    period,
    setPeriod,
    windows,
    analytics: {
      status: analyticsStatus,
      mode: analyticsData?.mode || null,
      playerLevel: analyticsData?.playerLevel || false,
      fetchedAt: analyticsData?.fetchedAt || null,
      error: analytics.key === requestKey ? analytics.error : null
    },
    metrics,
    players,
    loadPlayerTotals,
    playerCount: scope?.playerIds.length || 0,
    network,
    platform: {
      visible: isAdminGroup,
      status: platform.status,
      data: platform.data,
      error: platform.error
    },
    refresh,
    refreshing
  };
}
